/**
 * ForceGraph — D3 force-directed graph with zoom/pan and drag.
 *
 * Renders an SVG with nodes and edges positioned by d3-force simulation.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  zoom as d3Zoom,
  zoomIdentity,
  select,
  drag as d3Drag,
  type Simulation,
  type D3DragEvent,
} from 'd3';
import GraphNode from './GraphNode';
import GraphEdge from './GraphEdge';
import EdgeTooltip from './EdgeTooltip';
import type { SimNode, SimLink } from './types';
import type { IntegrationNode, IntegrationEdge } from '../../services/integration-map-api';
import { NODE_RADIUS } from './constants';

interface Props {
  nodes: IntegrationNode[];
  edges: IntegrationEdge[];
  onNodePositionSave: (nodeId: string, x: number, y: number) => void;
  onNodeClick: (node: IntegrationNode) => void;
}

const ForceGraph: React.FC<Props> = ({ nodes, edges, onNodePositionSave, onNodeClick }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null);

  const [simNodes, setSimNodes] = useState<SimNode[]>([]);
  const [simLinks, setSimLinks] = useState<SimLink[]>([]);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [tooltipEdge, setTooltipEdge] = useState<{ edge: IntegrationEdge; x: number; y: number } | null>(null);

  // Connected edges for highlighting
  const connectedEdgeIds = useRef<Set<string>>(new Set());
  const connectedNodeIds = useRef<Set<string>>(new Set());

  // Build simulation
  useEffect(() => {
    if (nodes.length === 0) return;

    const simNodesData: SimNode[] = nodes.map((n) => ({
      ...n,
      x: n.xPosition ?? 400 + Math.random() * 200 - 100,
      y: n.yPosition ?? 300 + Math.random() * 200 - 100,
      fx: n.xPosition ?? undefined,
      fy: n.yPosition ?? undefined,
    }));

    const nodeMap = new Map(simNodesData.map((n) => [n.id, n]));

    const simLinksData: SimLink[] = edges
      .filter((e) => nodeMap.has(e.sourceAreaId) && nodeMap.has(e.targetAreaId))
      .map((e) => ({
        source: e.sourceAreaId,
        target: e.targetAreaId,
        edge: e,
      }));

    const sim = forceSimulation<SimNode>(simNodesData)
      .force('link', forceLink<SimNode, SimLink>(simLinksData).id((d) => d.id).distance(160))
      .force('charge', forceManyBody().strength(-400))
      .force('center', forceCenter(400, 300))
      .force('collide', forceCollide(NODE_RADIUS + 30))
      .on('tick', () => {
        setSimNodes([...simNodesData]);
        setSimLinks([...simLinksData]);
      });

    // After simulation stabilizes, release fixed positions for nodes without saved positions
    sim.on('end', () => {
      simNodesData.forEach((n) => {
        if (n.xPosition == null) {
          n.fx = undefined;
          n.fy = undefined;
        }
      });
    });

    simRef.current = sim;

    return () => {
      sim.stop();
    };
  }, [nodes, edges]);

  // Setup zoom/pan
  useEffect(() => {
    if (!svgRef.current || !gRef.current) return;

    const svgEl = select(svgRef.current);
    const gEl = select(gRef.current);

    const zoomBehavior = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        gEl.attr('transform', event.transform.toString());
      });

    svgEl.call(zoomBehavior);

    // Initial fit
    svgEl.call(zoomBehavior.transform, zoomIdentity.translate(0, 0).scale(0.9));

    return () => {
      svgEl.on('.zoom', null);
    };
  }, []);

  // Setup drag
  useEffect(() => {
    if (!svgRef.current || !simRef.current) return;

    const sim = simRef.current;
    const svgEl = select(svgRef.current);

    const dragBehavior = d3Drag<SVGGElement, SimNode>()
      .on('start', (event: D3DragEvent<SVGGElement, SimNode, SimNode>, d: SimNode) => {
        if (!event.active) sim.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (event: D3DragEvent<SVGGElement, SimNode, SimNode>, d: SimNode) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event: D3DragEvent<SVGGElement, SimNode, SimNode>, d: SimNode) => {
        if (!event.active) sim.alphaTarget(0);
        d.fx = event.x;
        d.fy = event.y;
        onNodePositionSave(d.id, event.x, event.y);
      });

    // Apply drag to node groups
    svgEl.selectAll<SVGGElement, SimNode>('.integration-node').call(dragBehavior);

    return () => {
      svgEl.selectAll<SVGGElement, SimNode>('.integration-node').on('.drag', null);
    };
  }, [simNodes, onNodePositionSave]);

  const handleNodeHover = useCallback((node: SimNode) => {
    setHoveredNodeId(node.id);
    // Build connected sets
    const eids = new Set<string>();
    const nids = new Set<string>([node.id]);
    edges.forEach((e) => {
      if (e.sourceAreaId === node.id || e.targetAreaId === node.id) {
        eids.add(e.id);
        nids.add(e.sourceAreaId);
        nids.add(e.targetAreaId);
      }
    });
    connectedEdgeIds.current = eids;
    connectedNodeIds.current = nids;
  }, [edges]);

  const handleNodeLeave = useCallback(() => {
    setHoveredNodeId(null);
    connectedEdgeIds.current.clear();
    connectedNodeIds.current.clear();
  }, []);

  const handleEdgeHover = useCallback((e: React.MouseEvent, edge: IntegrationEdge) => {
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (svgRect) {
      setTooltipEdge({ edge, x: e.clientX - svgRect.left, y: e.clientY - svgRect.top });
    }
  }, []);

  const handleEdgeLeave = useCallback(() => {
    setTooltipEdge(null);
  }, []);

  // Resolve link endpoints to nodes
  const resolvedLinks = simLinks.map((l) => ({
    ...l,
    sourceNode: typeof l.source === 'object' ? l.source : simNodes.find((n) => n.id === l.source),
    targetNode: typeof l.target === 'object' ? l.target : simNodes.find((n) => n.id === l.target),
  }));

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '400px' }}>
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{ background: 'transparent', borderRadius: '8px' }}
      >
        <defs>
          <marker
            id="arrow"
            viewBox="0 -4 8 8"
            refX={NODE_RADIUS + 8}
            refY={0}
            markerWidth={6}
            markerHeight={6}
            orient="auto"
          >
            <path d="M0,-4L8,0L0,4" fill="#64748b" opacity={0.6} />
          </marker>
        </defs>
        <g ref={gRef}>
          {/* Edges */}
          <g className="edges">
            {resolvedLinks.map((l) => {
              if (!l.sourceNode || !l.targetNode) return null;
              const isHighlighted = hoveredNodeId != null && connectedEdgeIds.current.has(l.edge.id);
              const isDimmed = hoveredNodeId != null && !connectedEdgeIds.current.has(l.edge.id);
              return (
                <GraphEdge
                  key={l.edge.id}
                  edge={l.edge}
                  source={l.sourceNode}
                  target={l.targetNode}
                  highlighted={isHighlighted}
                  dimmed={isDimmed}
                  onMouseEnter={handleEdgeHover}
                  onMouseLeave={handleEdgeLeave}
                />
              );
            })}
          </g>
          {/* Nodes */}
          <g className="nodes">
            {simNodes.map((n) => {
              const isHighlighted = hoveredNodeId === n.id;
              const isDimmed = hoveredNodeId != null && !connectedNodeIds.current.has(n.id);
              return (
                <g key={n.id} className="integration-node" data-node-id={n.id}>
                  <GraphNode
                    node={n}
                    highlighted={isHighlighted}
                    dimmed={isDimmed}
                    onMouseEnter={handleNodeHover}
                    onMouseLeave={handleNodeLeave}
                    onClick={onNodeClick}
                  />
                </g>
              );
            })}
          </g>
        </g>
      </svg>

      {/* Edge tooltip */}
      {tooltipEdge && (
        <EdgeTooltip edge={tooltipEdge.edge} x={tooltipEdge.x} y={tooltipEdge.y} />
      )}
    </div>
  );
};

export default ForceGraph;
